<template>
  <div class="cpd">
    <router-link to="/portal/projects" class="back-link">← {{ t('portal.navProjects') }}</router-link>

    <div v-if="loading" class="spinner"></div>

    <template v-else-if="project">
      <div class="page-header">
        <div>
          <h1 class="page-title"><span class="dot" :style="{ background: project.color }"></span>{{ project.name }}</h1>
          <p v-if="plainDesc" class="page-sub">{{ plainDesc }}</p>
        </div>
        <select v-model="statusFilter" class="form-control status-sel">
          <option v-for="o in statusOpts" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </div>

      <!-- Billing snapshot -->
      <div class="bill-strip">
        <div class="bill"><div class="bv">{{ priceTypeLabel }}</div><div class="bl">{{ t('portal.billingModel') }}</div></div>
        <div class="bill"><div class="bv">{{ hours }}</div><div class="bl">{{ t('portal.hoursTracked') }}</div></div>
        <div class="bill"><div class="bv">{{ total }}</div><div class="bl">{{ t('portal.estBilling') }}</div></div>
      </div>

      <!-- Task table — same look as the internal tasks board. Custom fields are
           editable inline; everything else is read-only for clients. -->
      <div v-if="tasks.length === 0" class="empty-state"><p>{{ t('portal.noTasks') }}</p></div>
      <section v-else class="proj-table" :style="{ '--tt-cols': ttCols }">
        <div class="pt-head">
          <span class="pt-dot" :style="{ background: project.color || 'var(--border-2)' }"></span>
          <h3 class="pt-name">{{ t('portal.tasks') }}</h3>
          <span class="pt-count">{{ tasks.length }}</span>
        </div>

        <div class="pt-scroll">
          <div class="pt-cols">
            <span class="ptc">{{ t('teamReports.subject') }}</span>
            <span class="ptc">{{ t('teamReports.timeCol') }}</span>
            <span class="ptc">{{ t('taskForm.urgency') }}</span>
            <span v-for="f in fields" :key="f.id" class="ptc">{{ f.label }}</span>
            <span class="ptc"></span>
          </div>

          <div class="pt-rows">
            <TaskCard
              v-for="tk in tasks"
              :key="tk.id"
              :task="tk"
              :fields="fields"
              :columns="clientColumns"
              table
              @open="openTask(tk)"
              @field-value="onFieldValue"
            />
          </div>
        </div>
      </section>
    </template>

    <div v-else class="empty-state"><p>{{ t('portal.noProjects') }}</p></div>

    <!-- Read-only task detail — description, due date, subtasks, comments. -->
    <ClientTaskDrawer v-if="drawerTask" :task="drawerTask" @close="drawerTask = null" />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api'
import TaskCard from '@/components/TaskCard.vue'
import ClientTaskDrawer from '@/components/ClientTaskDrawer.vue'
import { useI18n } from '@/composables/useI18n'
import { useToast } from '@/composables/useToast'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { fmtMoney, projectTotal, projectHours } from '@/utils/billing'

export default {
  name: 'ClientProjectDetail',
  components: { TaskCard, ClientTaskDrawer },
  setup() {
    const route = useRoute()
    const { t } = useI18n()
    const toast = useToast()
    const pid = Number(route.params.id)
    const project = ref(null)
    const tasks = ref([])
    const fields = ref([])   // custom field defs visible here: this project's + global
    const loading = ref(true)
    const drawerTask = ref(null)

    // Same options as the internal board: default hides archived work; "all
    // tasks" widens the scope so previously completed tasks stay reviewable.
    const statusFilter = ref('')
    const statusOpts = computed(() => [
      { value: '',            label: t('common.all') },
      { value: 'open',        label: t('status.open') },
      { value: 'in_progress', label: t('status.in_progress') },
      { value: 'all',         label: t('tasks.allTasks') },
    ])

    const hours = computed(() => projectHours(project.value) + 'h')
    const total = computed(() => fmtMoney(projectTotal(project.value), project.value?.currency || 'ILS'))
    // Description may be rich text — show a plain-text excerpt (no HTML injection).
    const plainDesc = computed(() => {
      if (!project.value?.description) return ''
      const el = document.createElement('div')
      el.innerHTML = project.value.description
      return (el.textContent || '').trim()
    })

    const priceTypeLabel = computed(() => {
      const pt = project.value?.pricing_type
      return pt === 'fixed' ? t('projects.pricingFixed') : pt === 'hourly' ? t('projects.pricingHourly') : t('portal.noPricing')
    })

    // Column order for the client table: time, urgency, then the custom fields.
    // (No assignee column — team members aren't exposed in the portal.)
    const clientColumns = computed(() => ['time', 'urgency', ...fields.value.map(f => f.id)])
    // Same widths as the internal board: subject flex, time 150, urgency 116,
    // 130 per custom field, trailing status/action column 138.
    const ttCols = computed(() =>
      `minmax(220px,1fr) 150px 116px${fields.value.map(() => ' 130px').join('')} 138px`)

    function openTask(tk) { drawerTask.value = tk }

    // Optimistic inline value save — same pattern as the internal board. The
    // backend only honours field_values from external clients.
    async function onFieldValue({ task, fieldId, value }) {
      const prev = { ...(task.field_values || {}) }
      const next = { ...(task.field_values || {}) }
      if (value === '' || value == null) delete next[fieldId]; else next[fieldId] = value
      task.field_values = next
      try { await api.updateTask(task.id, { field_values: { [fieldId]: value } }) }
      catch { task.field_values = prev; toast.error(t('toast.saveFailed')) }
    }

    async function loadFields() {
      try {
        const all = (await api.getProjectFields()).data || []
        fields.value = all
          .filter(f => f.project_id === null || f.project_id === pid)
          .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
      } catch {}
    }

    async function load() {
      loading.value = true
      try {
        const params = { project_id: pid }
        if (statusFilter.value === 'all') params.scope = 'all'
        else if (statusFilter.value) params.status = statusFilter.value
        const [pr, tr] = await Promise.all([api.getProjects(), api.getTasks(params)])
        project.value = (pr.data || []).find(p => p.id === pid) || null
        tasks.value = tr.data || []
      } catch {} finally { loading.value = false }
    }

    watch(statusFilter, () => load())

    let off = () => {}
    let offFields = () => {}
    onMounted(() => {
      load(); loadFields()
      off = onRealtime('task.changed', debounce(() => load(), 400))
      offFields = onRealtime('project.changed', debounce(loadFields, 400))
    })
    onUnmounted(() => { off(); offFields() })

    return {
      t, project, tasks, fields, loading, drawerTask, openTask, statusFilter, statusOpts,
      hours, total, priceTypeLabel, plainDesc,
      clientColumns, ttCols, onFieldValue,
    }
  },
}
</script>

<style scoped>
.back-link { font-size: 13px; color: var(--muted); text-decoration: none; display: inline-block; margin-bottom: 14px; }
.back-link:hover { color: var(--accent); }
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.status-sel { max-width: 170px; }
.dot { display: inline-block; width: 12px; height: 12px; border-radius: 4px; margin-inline-end: 8px; vertical-align: middle; }

.bill-strip {
  display: grid; grid-template-columns: repeat(3, 1fr);
  border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface);
  overflow: hidden; margin: 18px 0 28px;
}
.bill { padding: 14px 18px; }
.bill + .bill { border-inline-start: 1px solid var(--border); }
.bv { font-size: 22px; font-weight: 600; font-variant-numeric: tabular-nums; }
.bl { font-size: 11px; color: var(--muted); margin-top: 5px; }

/* Project task table — mirrors the internal tasks board (.proj-table family
   in TasksView; those styles are scoped there, so the box is restyled here). */
.proj-table {
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(20,24,40,.07);
}
.pt-head { display: flex; align-items: center; gap: 11px; padding: 14px 18px; }
.pt-dot { width: 11px; height: 11px; border-radius: 4px; flex-shrink: 0; }
.pt-name { font-size: var(--fs-h4); font-weight: 800; letter-spacing: -.01em; }
.pt-count {
  min-width: 22px; height: 22px; padding: 0 6px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--danger); color: #fff;
  border-radius: 999px; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums;
}

.pt-scroll { overflow-x: auto; }
.pt-scroll > .pt-cols,
.pt-scroll > .pt-rows { min-width: min-content; }

.pt-cols {
  display: grid; grid-template-columns: var(--tt-cols);
  background: var(--surface-2);
  border-block: 1px solid var(--row-line);
}
.ptc {
  padding: 8px 14px; font-size: 13px; font-weight: 700;
  color: var(--text); text-transform: uppercase; letter-spacing: .03em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;
  border-inline-end: 1px solid var(--row-line);
}
.ptc:last-child { border-inline-end: none; }

@media (max-width: 760px) {
  .pt-cols { display: none; }
}
@media (max-width: 768px) {
  .bill-strip { grid-template-columns: 1fr; }
  .bill + .bill { border-inline-start: none; border-top: 1px solid var(--border); }
}
</style>
